import { datadogLogs } from "@datadog/browser-logs";
import type { ProfileInDB, ProfileUpdate } from "@profile-service-types/index.ts";
import type { UseMutationOptions } from "@tanstack/vue-query";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import type { Ref } from "vue";
import { unref } from "vue";
import type { UndefinedToNull } from "@/components/Account/types";
import profileService from "@/services/profile";
import { getQueryKey } from "./useGetSingleProfile";

type UseUpdateProfileArgs = {
  id: Ref<string>;
  payload: UndefinedToNull<ProfileUpdate>;
};

export const useUpdateProfile = (
  useMutationOptions?: UseMutationOptions<ProfileInDB, Error, UseUpdateProfileArgs>,
) => {
  const queryClient = useQueryClient();

  return useMutation<ProfileInDB, Error, UseUpdateProfileArgs>({
    mutationFn: async ({ id, payload }) => {
      const response = await profileService.updateProfile(unref(id), payload);
      return response?.data?.data;
    },
    onSuccess: async (data, { id }) => {
      await queryClient.invalidateQueries({ queryKey: getQueryKey(id) });
    },
    onError: (error, variables) => {
      datadogLogs.logger.warn("Failed to update profile.", {
        error: error.message,
        courseApprovalCount: unref(variables.id),
      });
    },
    ...useMutationOptions,
  });
};
